import { parseIsoCached } from "@/lib/utils";
import {
  getMessageTakedown,
  isMessageHidden,
  parseMessageData,
} from "@/lib/utils/parse-message-data";

export const GIF_CUSTOM_TYPE = "GIF_MESSAGE";
export const SUPERVISION_CUSTOM_TYPE = "SUPERVISION";
export const ACTIVITY_CUSTOM_TYPE = "ACTIVITY";
export const ENTITY_SHARED_CUSTOM_TYPE = "ENTITY_SHARED";
export const ENTITY_INTERACTION_CUSTOM_TYPE = "ENTITY_INTERACTION";
export const ACTIVITY_INTERACTION_CUSTOM_TYPE = "ACTIVITY_INTERACTION";
export const GAME_START_CUSTOM_TYPE = "GAME_START";
export const GAME_END_CUSTOM_TYPE = "GAME_END";

export type AttentionDirection = "incoming" | "outgoing" | "system";

export type AttentionSenderRole = "mentor" | "mentee" | "system" | "unknown";

// Read receipts aren't on the raw message (resolved from channel state), so
// incoming messages default to "unknown".
export type AttentionReadState =
  | "unknown"
  | "sent"
  | "delivered"
  | "read";

// In-flight delivery state, distinct from AttentionReadState (a read receipt).
export type AttentionSendState = "sent" | "pending" | "failed";

export interface AttentionSender {
  id: string | null;
  nickname: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  role: AttentionSenderRole;
}

export type AttentionMediaType = "image" | "video" | "audio";

export interface AttentionImageMedia {
  mediaType: "image";
  url: string | null;
  mimeType: string | null;
  thumbnailUrl: string | null;
  // Intrinsic dimensions so the thread can reserve the image box before load,
  // avoiding the layout shift that otherwise strands the initial scroll.
  width: number | null;
  height: number | null;
  alt: string | null;
  pending: boolean;
}

export interface AttentionVideoMedia {
  mediaType: "video";
  url: string | null;
  mimeType: string | null;
  posterUrl: string | null;
  pending: boolean;
}

export interface AttentionAudioMedia {
  mediaType: "audio";
  url: string | null;
  mimeType: string | null;
  isVoiceNote: boolean;
  /** Sendbird message id as string when confirmed (`messageId > 0`); null while pending. */
  sdkId: string | null;
  reqId: string | null;
  /** Not carried by Sendbird; resolved client-side from the decoded audio. */
  durationSeconds: number | null;
  /** Not carried by Sendbird; resolved client-side from the decoded audio. */
  peaks: number[] | null;
  transcription: string | null;
  pending: boolean;
}

export type AttentionMedia =
  | AttentionImageMedia
  | AttentionVideoMedia
  | AttentionAudioMedia;

export type AttentionEntityType = string;

export type AttentionActivityStatus =
  | "sent"
  | "in_progress"
  | "completed"
  | "declined";

export interface AttentionActivityPayload {
  entityId: string | null;
  activityType: string | null;
  status: AttentionActivityStatus | null;
  result: unknown;
  rawData: string | null;
}

export interface AttentionEntityPayload {
  entityId: string | null;
  entityType: AttentionEntityType | null;
  swapAction: string | null;
  previewUrl: string | null;
  secondPreviewUrl: string | null;
  notePreview: string | null;
  rawData: string | null;
}

export interface AttentionTakedown {
  alertId: string | null;
  actorRole: string | null;
  sTier: string | null;
  suppressForRecipient: boolean;
}

export type AttentionContent =
  | {
      type: "text";
      text: string;
      translations: Record<string, string> | null;
      rawData: string | null;
    }
  | {
      type: "supervision";
      text: string;
      translations: Record<string, string> | null;
    }
  | { type: "gif"; url: string }
  | { type: "media"; media: AttentionMedia; caption: string | null }
  | { type: "system"; text: string }
  | {
      type: "activity";
      text: string;
      customType: string;
      rawData: string | null;
      activity: AttentionActivityPayload;
    }
  | {
      type: "entityShared";
      text: string;
      customType: string;
      rawData: string | null;
      entity: AttentionEntityPayload;
    }
  | {
      type: "entityInteraction";
      text: string;
      customType: string;
      rawData: string | null;
      entity: AttentionEntityPayload;
    }
  | {
      type: "game";
      customType: string;
      phase: "start" | "end";
      rawData: string | null;
    }
  | { type: "hidden" }
  | { type: "takedown"; takedown: AttentionTakedown }
  | {
      type: "unsupported";
      text: string;
      customType: string | null;
      rawData: string | null;
    };

export type AttentionContentType = AttentionContent["type"];

export type AttentionQuotePreviewKind =
  | "text"
  | "image"
  | "video"
  | "audio"
  | "gif"
  | "entity"
  | "system"
  | "other";

export interface AttentionQuote {
  id: string;
  senderLabel: string;
  previewKind: AttentionQuotePreviewKind;
  text: string;
  thumbnailUrl: string | null;
}

export interface AttentionMessage {
  id: string;
  direction: AttentionDirection;
  isSelf: boolean;
  sender: AttentionSender | null;
  /** ISO 8601 timestamp (Luxon). */
  createdAt: string;
  readState: AttentionReadState;
  sendState: AttentionSendState;
  content: AttentionContent;
  quote: AttentionQuote | null;
  /** Original Sendbird custom type, retained for analytics/debug. */
  rawCustomType: string | null;
}

/** Direction input contract: who is "self" for a given viewer. */
export interface AttentionViewerContext {
  currentUserId: string;
}

export function resolveAttentionDirection(
  senderId: string | null,
  currentUserId: string,
): AttentionDirection {
  if (senderId === null) return "system";
  return senderId === currentUserId ? "outgoing" : "incoming";
}

/** Grouping inputs the thread view uses to collapse consecutive messages. */
export interface AttentionGroupingInput {
  senderId: string | null;
  direction: AttentionDirection;
  sentAtMs: number;
}

export function getAttentionGroupingInput(
  message: AttentionMessage,
): AttentionGroupingInput {
  const parsed = parseIsoCached(message.createdAt);
  return {
    senderId: message.sender?.id ?? null,
    direction: message.direction,
    sentAtMs: parsed.isValid ? parsed.toMillis() : 0,
  };
}

export function normalizeSenderRole(
  role: string | null | undefined,
): AttentionSenderRole {
  if (!role) return "unknown";
  return role.toUpperCase() === "MENTEE" ? "mentee" : "mentor";
}

interface RawEntityData {
  entity_id?: string | null;
  entity_type?: string | null;
  swap_action?: string | null;
  preview_url?: string | null;
  second_preview_url?: string | null;
  note_preview?: string | null;
}

export function parseAttentionEntityPayload(
  data: string | undefined,
): AttentionEntityPayload {
  const parsed = parseMessageData<RawEntityData>(data);
  return {
    entityId: parsed?.entity_id ?? null,
    entityType: parsed?.entity_type ?? null,
    swapAction: parsed?.swap_action ?? null,
    previewUrl: parsed?.preview_url ?? null,
    secondPreviewUrl: parsed?.second_preview_url ?? null,
    notePreview: parsed?.note_preview ?? null,
    rawData: data ?? null,
  };
}

interface RawActivityData {
  entity_id?: string | null;
  activity_type?: string | null;
  status?: string | null;
  result?: unknown;
}

const ACTIVITY_STATUSES: readonly AttentionActivityStatus[] = [
  "sent",
  "in_progress",
  "completed",
  "declined",
];

function toActivityStatus(
  status: string | null | undefined,
): AttentionActivityStatus | null {
  return ACTIVITY_STATUSES.find((candidate) => candidate === status) ?? null;
}

export function parseAttentionActivityPayload(
  data: string | undefined,
): AttentionActivityPayload {
  const parsed = parseMessageData<RawActivityData>(data);
  return {
    entityId: parsed?.entity_id ?? null,
    activityType: parsed?.activity_type ?? null,
    status: toActivityStatus(parsed?.status),
    result: parsed?.result ?? null,
    rawData: data ?? null,
  };
}

export function parseAttentionTakedown(
  data: string | undefined,
): AttentionTakedown | null {
  const takedown = getMessageTakedown(data);
  if (!takedown) return null;
  return {
    alertId: takedown.takedown_alert_id ?? null,
    actorRole: takedown.takedown_actor_role ?? null,
    sTier: takedown.takedown_s_tier ?? null,
    suppressForRecipient: takedown.suppress_for_recipient === true,
  };
}

export function isAttentionHidden(data: string | undefined): boolean {
  return isMessageHidden(data);
}
